import React, { useState } from 'react'
import toast from 'react-hot-toast'
import { usePostHog } from 'posthog-js/react'

/**
 * HubSpot Forms API submission
 *
 * Replace these two values with your actual HubSpot portal and form IDs:
 *   - PORTAL_ID: Found in HubSpot under Settings > Account > Hub ID
 *   - FORM_ID:   Found when you create a form in Marketing > Forms
 */
const PORTAL_ID = '245985243'
const FORM_ID = '1a6d10e7-7a0f-44b9-875d-23acba97b18f'

const ContactForm: React.FC = () => {
  const [name, setName] = useState<string>('')
  const [email, setEmail] = useState<string>('')
  const [message, setMessage] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(false)
  const [success, setSuccess] = useState<boolean>(false)
  const posthog = usePostHog()

  const handleSubmit = async () => {
    if (!email.trim()) {
      toast.error('Please enter your email address.')
      return
    }

    setLoading(true)

    try {
      const response = await fetch(
        `https://api.hsforms.com/submissions/v3/integration/submit/${PORTAL_ID}/${FORM_ID}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            fields: [
              { name: 'firstname', value: name },
              { name: 'email', value: email },
              { name: 'message', value: message }
            ],
            context: {
              pageUri: window.location.href,
              pageName: document.title
            }
          })
        }
      )

      if (!response.ok) throw new Error('Submission failed')

      posthog?.identify(email, { name, email })
      posthog?.capture('contact_form_submitted', { name, has_message: message.trim().length > 0 })

      setSuccess(true)
      setName('')
      setEmail('')
      setMessage('')

      setTimeout(() => setSuccess(false), 4000)
    } catch (err) {
      posthog?.capture('contact_form_failed')
      posthog?.captureException(err)
      toast.error('Something went wrong. Please try again or email us at hello@zeigersoftware.com')
    } finally {
      setLoading(false)
    }
  }

  const inputClass =
    'w-full px-4 py-[14px] border-[1.5px] border-black/10 rounded-[10px] font-bd text-[15px] text-ink bg-surface outline-none focus:border-pop focus:shadow-[0_0_0_3px_rgba(37,83,232,0.06)] transition-all placeholder:text-ink4'

  return (
    <section className='py-24 scroll-mt-20 px-4 md:px-10 text-center bg-[rgba(37,83,232,0.06)]' id='contact'>
      <div className='max-w-[580px] mx-auto'>
        <h2 className='font-hd text-[30px] md:text-[46px] font-extrabold tracking-[-1.5px] md:tracking-[-2px] leading-[1.06] mb-4'>
          Let&rsquo;s talk about <em className='not-italic text-pop'>your project.</em>
        </h2>
        <p className='text-[16px] text-ink2 leading-[1.7] mb-10'>
          15-minute call, no commitment, no sales pitch. We&rsquo;ll tell you honestly whether
          we&rsquo;re the right fit — and if we&rsquo;re not, we&rsquo;ll point you to someone who is.
        </p>

        <div className='max-w-[480px] mx-auto text-left'>
          <div className='grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5'>
            <div>
              <label
                htmlFor='cf-name'
                className='block text-[13px] font-semibold text-ink2 mb-[0.4rem] tracking-[0.2px]'
              >
                Name
              </label>
              <input
                type='text'
                id='cf-name'
                placeholder='Your name'
                value={name}
                onChange={(e) => setName(e.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label
                htmlFor='cf-email'
                className='block text-[13px] font-semibold text-ink2 mb-[0.4rem] tracking-[0.2px]'
              >
                Email
              </label>
              <input
                type='email'
                id='cf-email'
                placeholder='you@company.com'
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={inputClass}
              />
            </div>
          </div>

          <div className='mb-5'>
            <label
              htmlFor='cf-message'
              className='block text-[13px] font-semibold text-ink2 mb-[0.4rem] tracking-[0.2px]'
            >
              How can we help?
            </label>
            <textarea
              id='cf-message'
              placeholder='Tell us about your project, timeline, and budget — or just say hi.'
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={5}
              className={`${inputClass} resize-y min-h-[120px]`}
            />
          </div>

          <button
            type='button'
            onClick={handleSubmit}
            disabled={loading || success}
            style={success ? { background: '#16a34a' } : undefined}
            className='w-full inline-flex items-center justify-center gap-2 px-8 py-4 rounded-[10px] bg-pop text-white font-bd font-semibold text-[16px] border-none cursor-pointer hover:bg-pop-hover hover:-translate-y-px transition-all disabled:opacity-60 disabled:cursor-not-allowed disabled:translate-y-0 mt-2'
          >
            {loading ? 'Sending\u2026' : success ? "Sent! We'll be in touch \u2713" : 'Talk to us today \u2192'}
          </button>

          <p className='text-[12px] text-ink3 text-center mt-4'>
            We&rsquo;ll get back to you within one business day.
          </p>
        </div>
      </div>
    </section>
  )
}

export default ContactForm
